import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import CallbackFollowup from './CallbackFollowup';
import {
  announceCallbacksChanged, callbackCallId, callbackChatId, callbackSource, followupCopy, frontdeskTicketTypes, isCallbackOverdue,
} from './callbackExperience';
import {
  Box,
  Alert,
  AlertIcon,
  Button,
  Heading,
  Text,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  useToast,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  Select,
  Textarea,
  useDisclosure,
  Stack,
  InputGroup,
  InputLeftElement,
  Card,
  CardBody,
  Checkbox,
  HStack,
  Tooltip,
} from '@chakra-ui/react';
import { FiMoreVertical, FiPhone, FiCheck, FiX, FiClock, FiSearch, FiPlus, FiRefreshCw } from 'react-icons/fi';
import { useTenant } from '../../contexts/TenantContext';
import { formatInTenantTz } from '../../utils/formatDate';
import { CallbackRequestsAPI } from '../../services/api';
import PageHelp from '../../components/PageHelp';
import { useLanguage } from '../../i18n';
import { getPortalPageCopy } from '../../portalPageCopy';

const statusColors = {
  pending: 'yellow',
  contacted: 'blue',
  completed: 'green',
  cancelled: 'red',
};

const priorityColors = {
  urgent: 'red',
  high: 'orange',
  normal: 'blue',
  low: 'gray',
};

const ticketTypeLabels = {
  callback: 'Callback',
  email_back: 'Email Back',
  ecommerce_support: 'Ecommerce Support',
  product_compatibility_review: 'Compatibility Review',
  return_or_refund: 'Return / Refund',
  replacement: 'Replacement',
  missing_item: 'Missing Item',
  damaged_item: 'Damaged Item',
  bulk_purchase: 'Bulk Purchase',
  invoice_request: 'Invoice Request',
  human_escalation: 'Human Escalation',
  other: 'Other',
};

const issueTypeLabels = {
  department_not_configured: 'Department not configured',
  transfer_failed: 'Transfer failed',
  no_transfer_target: 'No transfer target',
  callback_requested: 'Callback requested',
  product_question: 'Product question',
  return_or_refund: 'Return or refund',
  replacement: 'Replacement',
  missing_item: 'Missing item',
  damaged_item: 'Damaged item',
};

const failReasonLabels = {
  department_not_configured: 'Department routing is not configured',
  transfer_target_not_found: 'Transfer target was not found',
  transfer_unavailable: 'Transfer target was unavailable',
  no_department_match: 'No matching department was found',
  no_contact_match: 'No matching contact was found',
};

const formatHumanLabel = (value, dictionary = {}) => {
  if (!value) return '';
  const raw = String(value).trim();
  if (!raw) return '';
  if (dictionary[raw]) return dictionary[raw];
  return raw
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .replace(/\b\w/g, (char) => char.toUpperCase());
};

const sanitizeTicketText = (value, issueLabels = issueTypeLabels, failLabels = failReasonLabels) => {
  if (!value) return '';
  let text = String(value);
  Object.entries({ ...issueLabels, ...failLabels }).forEach(([code, label]) => {
    text = text.replaceAll(code, label.toLowerCase());
  });
  return text;
};

const getTicketCustomerEmail = (req) => {
  if (!req) return '';
  return (
    req.customer_email ||
    req.collected_fields?.customer_email ||
    req.collected_fields?.email ||
    req.metadata?.customer_email ||
    req.metadata?.email ||
    ''
  );
};

const getTicketSearchText = (req, typeLabels = ticketTypeLabels) => ([
  req.assignee,
  req.notes,
  req.caller_name,
  req.caller_number,
  getTicketCustomerEmail(req),
  req.summary,
  req.customer_description,
  req.original_target,
  req.ticket_type,
  typeLabels[req.ticket_type],
  req.issue_type,
  req.product_name,
  req.order_number,
  req.platform,
  req.category,
  req.qa_id,
  req.requested_action,
  req.ai_summary,
  req.device_model,
  req.urgency,
].filter(Boolean).join(' ').toLowerCase());

const CreateTicketModal = ({ isOpen, onClose, onSuccess, copy, ticketTypeOptions, businessType, experience, callSessionId }) => {
  const initialFormData = {
    ticket_type: 'callback',
    session_id: callSessionId || 'manual',
    call_session_id: callSessionId || null,
    original_target: '',
    caller_name: '',
    caller_number: '',
    customer_email: '',
    summary: '',
    priority: 'normal',
    platform: '',
    order_number: '',
    product_name: '',
    issue_type: '',
    requested_action: '',
  };
  const [formData, setFormData] = useState({
    ...initialFormData,
  });
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const handleSubmit = async () => {
    if (!formData.caller_name || !formData.caller_number) {
      toast({
        title: copy.error,
        description: copy.nameNumberRequired,
        status: 'error',
        duration: 3000,
      });
      return;
    }
    if (formData.ticket_type === 'email_back' && !formData.customer_email) {
      toast({
        title: copy.error,
        description: copy.emailRequired,
        status: 'error',
        duration: 3000,
      });
      return;
    }

    setLoading(true);
    try {
      await CallbackRequestsAPI.create(formData);
      toast({
        title: copy.success,
        description: copy.ticketCreated,
        status: 'success',
        duration: 3000,
      });
      onSuccess();
      onClose();
      setFormData({ ...initialFormData });
    } catch (error) {
      toast({
        title: copy.error,
        description: error.response?.data?.detail || copy.createFailed,
        status: 'error',
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{copy.newTicketTitle}</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <Stack spacing={4}>
            <FormControl>
              <FormLabel>{copy.type}</FormLabel>
              <Select
                value={formData.ticket_type}
                onChange={(e) => setFormData({ ...formData, ticket_type: e.target.value })}
              >
                {ticketTypeOptions.map(([value, label]) => (
                  <option key={value} value={value}>{label}</option>
                ))}
              </Select>
            </FormControl>
            <FormControl isRequired>
              <FormLabel>{copy.customerName}</FormLabel>
              <Input
                value={formData.caller_name}
                onChange={(e) => setFormData({ ...formData, caller_name: e.target.value })}
                placeholder={copy.customerNamePlaceholder}
              />
            </FormControl>
            <FormControl isRequired>
              <FormLabel>{copy.phoneNumber}</FormLabel>
              <Input
                value={formData.caller_number}
                onChange={(e) => setFormData({ ...formData, caller_number: e.target.value })}
                placeholder="+1234567890"
              />
            </FormControl>
            <FormControl isRequired={formData.ticket_type === 'email_back'}>
              <FormLabel>{copy.customerEmail}</FormLabel>
              <Input
                type="email"
                value={formData.customer_email}
                onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                placeholder="customer@example.com"
              />
            </FormControl>
            <FormControl>
              <FormLabel>{copy.priority}</FormLabel>
              <Select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value })}
              >
                <option value="urgent">{copy.urgent}</option>
                <option value="high">{copy.high}</option>
                <option value="normal">{copy.normal}</option>
                <option value="low">{copy.low}</option>
              </Select>
            </FormControl>
            {businessType === 'ecommerce' && <>
            <Stack direction={{ base: 'column', md: 'row' }} spacing={3}>
              <FormControl>
                <FormLabel>{copy.platform}</FormLabel>
                <Input
                  value={formData.platform}
                  onChange={(e) => setFormData({ ...formData, platform: e.target.value })}
                  placeholder={copy.platformPlaceholder}
                />
              </FormControl>
              <FormControl>
                <FormLabel>{copy.orderNumber}</FormLabel>
                <Input
                  value={formData.order_number}
                  onChange={(e) => setFormData({ ...formData, order_number: e.target.value })}
                  placeholder={copy.optional}
                />
              </FormControl>
            </Stack>
            <Stack direction={{ base: 'column', md: 'row' }} spacing={3}>
              <FormControl>
                <FormLabel>{copy.product}</FormLabel>
                <Input
                  value={formData.product_name}
                  onChange={(e) => setFormData({ ...formData, product_name: e.target.value })}
                  placeholder={copy.productPlaceholder}
                />
              </FormControl>
              <FormControl>
                <FormLabel>{copy.issueType}</FormLabel>
                <Input
                  value={formData.issue_type}
                  onChange={(e) => setFormData({ ...formData, issue_type: e.target.value })}
                  placeholder={copy.issueTypePlaceholder}
                />
              </FormControl>
            </Stack>
            </>}
            <FormControl><FormLabel>{experience.target}</FormLabel><Input value={formData.original_target} onChange={e => setFormData({ ...formData, original_target: e.target.value })} /></FormControl>
            <FormControl>
              <FormLabel>{copy.requestedAction}</FormLabel>
              <Input
                value={formData.requested_action}
                onChange={(e) => setFormData({ ...formData, requested_action: e.target.value })}
                placeholder={copy.actionPlaceholder}
              />
            </FormControl>
            <FormControl>
              <FormLabel>{copy.issueSummary}</FormLabel>
              <Textarea
                value={formData.summary}
                onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                placeholder={copy.summaryPlaceholder}
              />
            </FormControl>
          </Stack>
        </ModalBody>
        <ModalFooter>
          <Button variant="ghost" mr={3} onClick={onClose}>{copy.cancel}</Button>
          <Button colorScheme="blue" onClick={handleSubmit} isLoading={loading}>{copy.create}</Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const PAGE_SIZE = 20;

const CallbackRequests = ({ businessType = 'front_desk', portalBasePath = '/app' }) => {
  const [searchParams] = useSearchParams();
  const callSessionId = searchParams.get('call_session_id');
  const { lang } = useLanguage();
  const experience = followupCopy(lang);
  const baseCopy = getPortalPageCopy(lang, 'supportTickets');
  // In the ecommerce workspace this page is the 回电请求 tab; the workspace
  // header already shows that title and what the page is for.
  const copy = businessType === 'ecommerce' ? {
    ...baseCopy, pageTitle: lang === 'zh' ? '回电请求' : 'Callback requests',
  } : {
    ...baseCopy, pageTitle: experience.title, pageSummary: experience.summary,
    newTicket: experience.create, newTicketTitle: experience.create,
    createTicket: experience.create, searchPlaceholder: experience.search,
    createTicketHelp: experience.summary, viewDetails: experience.detail,
  };
  const ticketTypeLabels = {
    callback: copy.callback,
    email_back: copy.emailBack,
    ecommerce_support: copy.ecommerceSupport,
    product_compatibility_review: copy.compatibilityReview,
    return_or_refund: copy.returnRefund,
    replacement: copy.replacement,
    missing_item: copy.missingItem,
    damaged_item: copy.damagedItem,
    bulk_purchase: copy.bulkPurchase,
    invoice_request: copy.invoiceRequest,
    human_escalation: copy.humanEscalation,
    other: copy.other,
  };
  const ticketTypeOptions = Object.entries(ticketTypeLabels);
  const createTicketTypeOptions = ticketTypeOptions.filter(
    ([value]) => value !== 'ecommerce_support' && (businessType === 'ecommerce' || frontdeskTicketTypes.has(value))
  );
  const statusLabels = {
    pending: copy.pending,
    contacted: copy.contacted,
    completed: copy.completed,
    cancelled: copy.cancelled,
  };
  const sourceLabels = { voice: experience.sourceVoice, web: experience.sourceWeb, manual: experience.sourceManual };
  const priorityLabels = {
    urgent: copy.urgent,
    high: copy.high,
    normal: copy.normal,
    low: copy.low,
  };
  const localizedIssueTypeLabels = {
    department_not_configured: copy.departmentNotConfigured,
    transfer_failed: copy.transferFailed,
    no_transfer_target: copy.noTransferTarget,
    callback_requested: copy.callbackRequested,
    product_question: copy.productQuestion,
    return_or_refund: copy.returnOrRefund,
    replacement: copy.replacement,
    missing_item: copy.missingItemIssue,
    damaged_item: copy.damagedItemIssue,
  };
  const localizedFailReasonLabels = {
    department_not_configured: copy.departmentRoutingMissing,
    transfer_target_not_found: copy.transferTargetNotFound,
    transfer_unavailable: copy.transferUnavailable,
    no_department_match: copy.noDepartmentMatch,
    no_contact_match: copy.noContactMatch,
  };
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  // Default to the work queue: pending requests, oldest first, so nothing waits unseen.
  const [filterStatus, setFilterStatus] = useState('pending');
  const [filterTicketType, setFilterTicketType] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(0);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const requestSequence = useRef(0);
  const [loadError, setLoadError] = useState(false);
  const [detailTicket, setDetailTicket] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const toast = useToast();
  const { tenantTimezone } = useTenant();
  const { isOpen, onOpen, onClose } = useDisclosure();

  const fetchRequests = useCallback(async () => {
    const sequence = ++requestSequence.current;
    setLoading(true);
    setLoadError(false);
    setRequests([]);
    setSelectedIds(new Set());
    try {
      const params = { limit: PAGE_SIZE, offset: page * PAGE_SIZE };
      if (callSessionId) params.call_session_id = callSessionId;
      if (filterStatus !== 'all') params.status = filterStatus;
      if (filterStatus === 'pending') params.order = 'oldest';
      if (filterTicketType !== 'all') params.ticket_type = filterTicketType;
      if (filterPriority !== 'all') params.priority = filterPriority;
      const data = await CallbackRequestsAPI.list(params);
      if (sequence !== requestSequence.current) return;
      setRequests(Array.isArray(data) ? data : []);
      announceCallbacksChanged();
    } catch (error) {
      if (sequence !== requestSequence.current) return;
      setLoadError(true);
      toast({
        title: copy.error,
        description: copy.loadFailed,
        status: 'error',
        duration: 3000,
      });
    } finally {
      if (sequence === requestSequence.current) setLoading(false);
    }
  }, [callSessionId, filterPriority, filterStatus, filterTicketType, page, toast, copy.error, copy.loadFailed]);

  useEffect(() => {
    fetchRequests();
    const sequenceRef = requestSequence;
    return () => { sequenceRef.current++; };
  }, [fetchRequests]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') fetchRequests();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [fetchRequests]);

  const handleStatusUpdate = async (id, newStatus) => {
    try {
      await CallbackRequestsAPI.updateStatus(id, newStatus);
      toast({
        title: copy.updated,
        description: `${copy.requestMarked} ${statusLabels[newStatus] || newStatus}`,
        status: 'success',
        duration: 2000,
      });
      fetchRequests();
    } catch (error) {
      toast({
        title: copy.error,
        description: copy.updateFailed,
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleBulkStatus = async (newStatus) => {
    if (loading || bulkLoading) return;
    const ids = filteredRequests.filter(request => selectedIds.has(request.id)).map(request => request.id);
    if (ids.length === 0) return;
    setBulkLoading(true);
    let done = 0;
    let failed = 0;
    for (const id of ids) {
      try {
        await CallbackRequestsAPI.updateStatus(id, newStatus);
        done++;
      } catch {
        failed++;
      }
    }
    setBulkLoading(false);
    setSelectedIds(new Set());
    fetchRequests();
    toast({
      title: copy.bulkUpdate,
      description: `${done} ${copy.updatedCount}${failed ? `, ${failed} ${copy.failedCount}` : ''}`,
      status: failed ? 'warning' : 'success',
      duration: 3000,
    });
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Filter by search query (client-side for current page)
  const filteredRequests = requests.filter(req => {
    const q = searchQuery.toLowerCase();
    return getTicketSearchText(req, ticketTypeLabels).includes(q);
  });

  useEffect(() => {
    setSelectedIds(new Set());
  }, [searchQuery, page, filterStatus, filterTicketType, filterPriority, callSessionId]);

  const selectedVisibleCount = filteredRequests.filter(request => selectedIds.has(request.id)).length;
  const toggleSelectAll = () => {
    if (selectedVisibleCount === filteredRequests.length) setSelectedIds(new Set());
    else setSelectedIds(new Set(filteredRequests.map((r) => r.id)));
  };


  return (
    <Box h={{ base: 'auto', lg: '100%' }} minH={0} display="flex" flexDirection="column" overflowY={{ base: 'visible', lg: 'hidden' }} p={5}>
      <PageHelp
        title={copy.pageTitle}
        description={copy.pageSummary}
        steps={[
          { title: copy.createTicket, detail: copy.createTicketHelp },
          { title: copy.filterSearch, detail: copy.filterSearchHelp },
          { title: copy.closeLoop, detail: copy.closeLoopHelp },
        ]}
      />
      <Stack direction={{ base: 'column', md: 'row' }} justify="space-between" align={{ base: 'flex-start', md: 'center' }} mb={4} spacing={3} flexShrink={0}>
        {businessType === 'ecommerce' ? <Box /> : <Box>
          <Heading size="lg">{copy.pageTitle}</Heading>
          <Text color="gray.500" mt={1}>{copy.pageSummary}</Text>
        </Box>}
        <HStack spacing={2} flexWrap="wrap">
          <Button
            leftIcon={<FiRefreshCw />}
            variant="outline"
            size="sm"
            onClick={() => fetchRequests()}
            isLoading={loading}
          >
            {copy.refresh}
          </Button>
            <Button leftIcon={<FiPlus />} size={{ base: 'sm', md: 'md' }} colorScheme="blue" onClick={onOpen}>
            {copy.newTicket}
          </Button>
        </HStack>
      </Stack>

      <Card variant="outline" bg="white" mb={4} flexShrink={0}>
        <CardBody>
          <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
            <InputGroup maxW="300px">
              <InputLeftElement pointerEvents="none">
                <FiSearch color="gray.300" />
              </InputLeftElement>
              <Input 
                placeholder={copy.searchPlaceholder}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>
            <Select 
              maxW="200px" 
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value); setPage(0); }}
            >
              <option value="all">{copy.allStatus}</option>
              <option value="pending">{copy.pending}</option>
              <option value="contacted">{copy.contacted}</option>
              <option value="completed">{copy.completed}</option>
              <option value="cancelled">{copy.cancelled}</option>
            </Select>
            <Select
              maxW="230px"
              value={filterTicketType}
              onChange={(e) => { setFilterTicketType(e.target.value); setPage(0); }}
            >
              <option value="all">{copy.allTypes}</option>
              {ticketTypeOptions.map(([value, label]) => (
                <option key={value} value={value}>{label}</option>
              ))}
            </Select>
            <Select
              maxW="180px"
              value={filterPriority}
              onChange={(e) => { setFilterPriority(e.target.value); setPage(0); }}
            >
              <option value="all">{copy.allPriority}</option>
              <option value="urgent">{copy.urgent}</option>
              <option value="high">{copy.high}</option>
              <option value="normal">{copy.normal}</option>
              <option value="low">{copy.low}</option>
            </Select>
            {selectedVisibleCount > 0 && (
              <HStack flexWrap="wrap" spacing={2}>
                <Button size="sm" onClick={() => handleBulkStatus('contacted')} isLoading={bulkLoading}>
                  {copy.markContacted} ({selectedVisibleCount})
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>{copy.clear}</Button>
              </HStack>
            )}
          </Stack>
        </CardBody>
      </Card>

      <Box flex={{ base: 'none', lg: '1' }} minH={0} maxH={{ base: '24rem', lg: 'none' }} overflow="auto" display="flex" flexDirection="column" bg="white" borderRadius="lg" boxShadow="sm" border="1px" borderColor="gray.200">
        <Table variant="simple" size="sm" tableLayout="fixed" minW="1120px">
          <colgroup>
            <col style={{ width: '44px' }} />
            <col style={{ width: '120px' }} />
            <col style={{ width: '190px' }} />
            <col />
            <col style={{ width: '110px' }} />
            <col style={{ width: '110px' }} />
            <col style={{ width: '150px' }} />
            <col style={{ width: '170px' }} />
          </colgroup>
          <Thead position="sticky" top={0} zIndex={1} bg="gray.50">
            <Tr>
              <Th px={2}>
                <Checkbox
                  isChecked={filteredRequests.length > 0 && selectedVisibleCount === filteredRequests.length}
                  isIndeterminate={selectedVisibleCount > 0 && selectedVisibleCount < filteredRequests.length}
                  onChange={toggleSelectAll}
                  aria-label={copy.selectAll}
                />
              </Th>
              <Th>{copy.type}</Th>
              <Th>{copy.customer}</Th>
              <Th>{copy.issueSummary}</Th>
              <Th>{copy.priority}</Th>
              <Th>{copy.status}</Th>
              <Th>{copy.created}</Th>
              <Th>{copy.actions}</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loadError ? (
              <Tr><Td colSpan={8}><Alert status="error"><AlertIcon />{copy.loadFailed}<Button ml={3} onClick={fetchRequests}>{lang === 'zh' ? '重试' : 'Retry'}</Button></Alert></Td></Tr>
            ) : loading ? (
              <Tr>
                <Td colSpan={8} textAlign="center" py={8}>{copy.loading}</Td>
              </Tr>
            ) : filteredRequests.length === 0 ? (
              <Tr>
                <Td colSpan={8} textAlign="center" py={8}>{copy.noTickets}</Td>
              </Tr>
            ) : (
              filteredRequests.map((req) => {
                const isHandled = req.status === 'completed' || req.status === 'contacted';
                const summaryText = sanitizeTicketText(req.summary || req.requested_action || '', localizedIssueTypeLabels, localizedFailReasonLabels);
                const customerEmail = getTicketCustomerEmail(req);
                const source = callbackSource(req);
                const overdue = isCallbackOverdue(req);
                return (
                  <Tr
                    key={req.id}
                    bg={isHandled ? 'gray.100' : 'white'}
                    color={isHandled ? 'gray.600' : undefined}
                    _hover={{ bg: isHandled ? 'gray.100' : 'gray.50' }}
                  >
                    <Td px={2}>
                      <Checkbox
                        isChecked={selectedIds.has(req.id)}
                        onChange={() => toggleSelect(req.id)}
                        aria-label={`${copy.selectTicket} ${req.caller_name}`}
                      />
                    </Td>
                    <Td>
                      <Stack spacing={1} align="flex-start">
                        <Badge colorScheme={req.ticket_type === 'callback' || !req.ticket_type ? 'blue' : 'purple'} variant="subtle" whiteSpace="normal">
                          {formatHumanLabel(req.ticket_type || 'callback', ticketTypeLabels)}
                        </Badge>
                        <Badge variant="outline" colorScheme={source === 'web' ? 'teal' : 'gray'} fontSize="2xs" data-testid="callback-source">
                          {sourceLabels[source]}
                        </Badge>
                      </Stack>
                    </Td>
                    <Td>
                      <Stack spacing={0}>
                        <Tooltip label={req.caller_name || ''} isDisabled={!req.caller_name}>
                          <Text fontWeight={isHandled ? 'normal' : 'medium'} noOfLines={1}>
                            {req.caller_name || copy.unknownCustomer}
                          </Text>
                        </Tooltip>
                        <Text fontSize="sm" color={isHandled ? 'gray.500' : 'gray.600'} noOfLines={1}>{req.caller_number}</Text>
                        {customerEmail && (
                          <Text fontSize="xs" color="gray.500" noOfLines={1} title={customerEmail}>{customerEmail}</Text>
                        )}
                      </Stack>
                    </Td>
                    <Td>
                      <Stack spacing={1}>
                        {(req.issue_type || req.product_name || req.order_number) && (
                          <Text fontSize="xs" color="gray.500" noOfLines={1} title={[formatHumanLabel(req.issue_type, localizedIssueTypeLabels), req.product_name, req.order_number].filter(Boolean).join(' · ')}>
                            {[formatHumanLabel(req.issue_type, localizedIssueTypeLabels), req.product_name, req.order_number].filter(Boolean).join(' · ')}
                          </Text>
                        )}
                        <Tooltip label={summaryText} isDisabled={!summaryText}>
                          <Text noOfLines={2} title={summaryText || undefined} fontSize="sm" lineHeight="1.35">
                            {summaryText || copy.followUpRequested}
                          </Text>
                        </Tooltip>
                        {req.original_target && (
                          <Text fontSize="xs" color="gray.500" noOfLines={1} title={req.original_target}>{copy.target}: {req.original_target}</Text>
                        )}
                        {(req.preferred_time_window || req.metadata?.sla?.text_en) && (
                          <Text fontSize="xs" color={overdue ? 'red.600' : 'gray.500'} noOfLines={1} data-testid="callback-promise">
                            {req.preferred_time_window ? `${lang === 'zh' ? '期望' : 'Preferred'}: ${req.preferred_time_window} · ` : ''}
                            {lang === 'zh' ? '承诺' : 'Promised'}: {lang === 'zh' ? (req.metadata?.sla?.text_zh || req.metadata?.sla?.text_en) : req.metadata?.sla?.text_en}
                          </Text>
                        )}
                        {req.fail_reason && (
                          <Text fontSize="xs" color="orange.600" noOfLines={1} title={formatHumanLabel(req.fail_reason, localizedFailReasonLabels)}>
                            {copy.needsSetup}: {formatHumanLabel(req.fail_reason, localizedFailReasonLabels)}
                          </Text>
                        )}
                        {req.assignee && <Text fontSize="xs" color="gray.600">{experience.assignee}: {req.assignee}</Text>}
                        {req.next_action && <Text fontSize="xs" noOfLines={2}>{experience.next}: {req.next_action}</Text>}
                      </Stack>
                    </Td>
                    <Td>
                      <Badge colorScheme={priorityColors[req.priority] || 'gray'} size="sm">
                        {formatHumanLabel(req.priority, priorityLabels)}
                      </Badge>
                    </Td>
                    <Td>
                      <Stack spacing={1} align="flex-start">
                        <Badge colorScheme={statusColors[req.status] || 'gray'} variant="subtle" size="sm">
                          {formatHumanLabel(req.status, statusLabels)}
                        </Badge>
                        {overdue && (
                          <Tooltip label={experience.overdueHint}>
                            <Badge colorScheme="red" variant="solid" size="sm" title={experience.overdueHint}>{experience.overdue}</Badge>
                          </Tooltip>
                        )}
                      </Stack>
                    </Td>
                    <Td fontSize="sm" color={overdue ? 'red.600' : undefined} fontWeight={overdue ? 'semibold' : undefined}>
                      {formatInTenantTz(req.created_at, tenantTimezone)}
                    </Td>
                    <Td>
                      <HStack spacing={2} flexWrap="wrap">
                        {!isHandled && (
                          <Button
                            size="sm"
                            colorScheme="green"
                            leftIcon={<FiCheck />}
                            onClick={() => setDetailTicket(req)}
                          >
                            {experience.detail}
                          </Button>
                        )}
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            aria-label={copy.options}
                            icon={<FiMoreVertical />}
                            variant="ghost"
                            size="sm"
                          />
                          <MenuList>
                            <MenuItem onClick={() => setDetailTicket(req)}>
                              {copy.viewDetails}
                            </MenuItem>
                            <MenuItem icon={<FiPhone />} onClick={() => window.location.href = `tel:${req.caller_number}`}>
                              {copy.call}
                            </MenuItem>
                            {req.status !== 'completed' && (
                              <MenuItem icon={<FiCheck />} onClick={() => setDetailTicket(req)}>
                                {experience.detail}
                              </MenuItem>
                            )}
                            {req.status === 'pending' && (
                              <MenuItem icon={<FiClock />} onClick={() => handleStatusUpdate(req.id, 'contacted')}>
                                {copy.markContacted}
                              </MenuItem>
                            )}
                            {req.status !== 'cancelled' && (
                              <MenuItem icon={<FiX />} color="red.500" onClick={() => handleStatusUpdate(req.id, 'cancelled')}>
                                {copy.cancel}
                              </MenuItem>
                            )}
                          </MenuList>
                        </Menu>
                      </HStack>
                    </Td>
                  </Tr>
                );
              })
            )}
          </Tbody>
        </Table>
      </Box>

      <HStack py={3} justify="space-between" align={{ base: 'flex-start', md: 'center' }} flexWrap="wrap" spacing={3} flexShrink={0}>
        <Text fontSize="sm" color="gray.500">{copy.page} {page + 1} ({copy.upTo} {PAGE_SIZE} {filterStatus === 'pending' ? copy.perPageOldest : copy.perPageNewest})</Text>
        <HStack flexWrap="wrap">
          <Button size="sm" isDisabled={page === 0} onClick={() => setPage((p) => p - 1)}>{copy.previous}</Button>
          <Button size="sm" isDisabled={loading || loadError || requests.length < PAGE_SIZE} onClick={() => setPage((p) => p + 1)}>{copy.next}</Button>
        </HStack>
      </HStack>

      <CreateTicketModal isOpen={isOpen} onClose={onClose} onSuccess={fetchRequests} copy={copy} ticketTypeOptions={createTicketTypeOptions} businessType={businessType} experience={experience} callSessionId={callSessionId} />
      <Modal isOpen={!!detailTicket} onClose={() => setDetailTicket(null)} size="xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{copy.ticketDetails}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {detailTicket && (
              <Stack spacing={4}>
                <HStack spacing={2} flexWrap="wrap">
                  <Badge colorScheme={detailTicket.ticket_type === 'callback' ? 'blue' : 'purple'}>
                    {ticketTypeLabels[detailTicket.ticket_type || 'callback'] || detailTicket.ticket_type}
                  </Badge>
                  <Badge colorScheme={priorityColors[detailTicket.priority] || 'gray'}>{priorityLabels[detailTicket.priority] || detailTicket.priority}</Badge>
                  <Badge colorScheme={statusColors[detailTicket.status] || 'gray'}>{statusLabels[detailTicket.status] || detailTicket.status}</Badge>
                  <Badge variant="outline">{sourceLabels[callbackSource(detailTicket)]}</Badge>
                  {isCallbackOverdue(detailTicket) && <Badge colorScheme="red" variant="solid">{experience.overdue}</Badge>}
                </HStack>
                <Box>
                  <Text fontSize="sm" color="gray.500">{copy.customer}</Text>
                  <Text>{detailTicket.caller_name || copy.unknown} · {detailTicket.caller_number || copy.noPhone}</Text>
                  <Text fontSize="sm">{copy.email}: {getTicketCustomerEmail(detailTicket) || copy.notAvailable}</Text>
                </Box>
                <Box>
                  <Text fontSize="sm" color="gray.500">{copy.issueSummary}</Text>
                  <Text whiteSpace="pre-wrap">{detailTicket.summary || detailTicket.customer_description || copy.noSummary}</Text>
                </Box>
                {detailTicket.original_target && <Text>{experience.target}: {detailTicket.original_target}</Text>}
                {detailTicket.requested_action && <Text>{copy.requestedAction}: {detailTicket.requested_action}</Text>}
                {businessType === 'ecommerce' && <Stack direction={{ base: 'column', md: 'row' }} spacing={4}>
                  <Box flex="1">
                    <Text fontSize="sm" color="gray.500">{copy.context}</Text>
                    <Text fontSize="sm">{copy.platform}: {detailTicket.platform || copy.notAvailable}</Text>
                    <Text fontSize="sm">{copy.order}: {detailTicket.order_number || copy.notAvailable}</Text>
                    <Text fontSize="sm">{copy.product}: {detailTicket.product_name || copy.notAvailable}</Text>
                    <Text fontSize="sm">{copy.device}: {detailTicket.device_model || copy.notAvailable}</Text>
                    <Text fontSize="sm">QA ID: {detailTicket.qa_id || copy.notAvailable}</Text>
                  </Box>
                  <Box flex="1">
                    <Text fontSize="sm" color="gray.500">{copy.handling}</Text>
                    <Text fontSize="sm">{copy.category}: {detailTicket.category || copy.notAvailable}</Text>
                    <Text fontSize="sm">{copy.issueType}: {detailTicket.issue_type || copy.notAvailable}</Text>
                    <Text fontSize="sm">{copy.requestedAction}: {detailTicket.requested_action || copy.notAvailable}</Text>
                    <Text fontSize="sm">{copy.urgency}: {detailTicket.urgency || copy.notAvailable}</Text>
                  </Box>
                </Stack>}
                <Box>
                  <Text fontSize="sm" color="gray.500">{copy.aiSummary}</Text>
                  <Text whiteSpace="pre-wrap">{detailTicket.ai_summary || copy.none}</Text>
                </Box>
                {callbackCallId(detailTicket) && <Button as={Link} to={`${portalBasePath}/call-history/${encodeURIComponent(callbackCallId(detailTicket))}`}>{experience.originalCall}</Button>}
                {callbackChatId(detailTicket) && <Button as={Link} to={`${portalBasePath}/call-history/chat/${encodeURIComponent(callbackChatId(detailTicket))}`}>{experience.originalChat}</Button>}
                <CallbackFollowup key={detailTicket.id} ticket={detailTicket} lang={lang} statusLabels={statusLabels} onSaved={updated => { setDetailTicket(updated); fetchRequests(); }} />
              </Stack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={() => setDetailTicket(null)}>{copy.close}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default CallbackRequests;
